import { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import _ from "underscore";
import { ICounterSale, ICSAncillary, IRaFillingForm, TLocales } from "../../models/models";
import {
	bookingDataValueState,
	counterSaleManagementValueState,
	listVehiclesReadyState,
	raFillingAccordionValidationState,
	recoveryRaModeState,
	rentalAgreementFillingValueState,
	stepState
} from "../state";
import {
	CREDIT_TYPE_VALUES,
	INVOICE_TYPES,
	RA_FILLING_FORM_INITIAL_VALUE,
	RA_FILLING_FORM_VALIDATION_SCHEMA, RA_FILLING_FORM_VALIDATION_SCHEMA_KEYLESS, SEGMENT_VALUES
} from "./config";
import { fetchCounterSaleManagement, fetchLegalEntities, raFilling as raFillingService, listVehiclesReadyToRent } from "../services/http.services";
import GeneralInfo from "./general-info/GeneralInfo";
import FinancialInfo from "./financial-info/FinancialInfo";
import BillingOwnerInfo from "./billing-owner-info/BillingOwnerInfo";
import AncillariesInfo from "./ancillaries-info/AncillariesInfo";
import QuotationInfo from "./quotation-info/QuotationInfo";
import { buildSelectedAcrissSipp } from "./selectedAcrissSipp";
import CustomLoader from "../../../../util/component/CustomLoader";
import { checkAncillaryUnitsMileage, formatCurrency, getLocaleByLang, isRetail } from "../../utils";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import {
	BILLING_OWNER_TYPE,
	COMPANY_CODES,
	FINANCIAL_PAYMENT_TIMING,
	FINANCIAL_PAYMENT_TIMING_CODES,
	LOCALE_ISO_DATE_TIME_FORMAT,
	LOCALES
} from "../../config";
import { ERROR_DETAIL_LIST, PA_ID_NUMBER_DEFAULT_VALUE } from "../config";
import { deepClone } from "../../../../util/state/commonFunctions";
import { currentBillingOwner, getChengesData, mapRaOwnerToPersonBillingOwner } from "../services/utils";
import { raFillingPricingErrorsManagement } from "./utils";
import { useSearchParams } from "react-router-dom";

const RaFilling = (props: { keyLess: boolean | undefined, keyLessAreas: any }) => {
	const { dispatchGlobalData, globalData } = useContext(GlobalContext);

	// LPP-22297: profili LPP dell'operatore loggato (dal grant), notificati a NTW/PRC per la governance
	// dello sconto al banco per-item (location x profili, IN), come idsProfile del Location Discount booking.
	const buildOperatorProfileIds = (): number[] =>
		((globalData?.user?.profiles ?? []) as any[])
			.map((p: any) => p?.id)
			.filter((id: any) => id !== null && id !== undefined);

	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const appLanguage = useTranslation();

	const [flagUpdateDefaultFormValue, setFlagUpdateDefaultFormValue] = useState<boolean>(false);
	const [flagCounterSaveGetOn, setFlagCounterSaveGetOn] = useState<any>(null);
	const [ntwOfferId, setNtwOfferId] = useState<string>(''); // flag che gestisce il salvataggio del ntwOfferId in localstorage

	const [step, setStep] = useRecoilState(stepState);
	const [raFilling, setRaFilling] = useRecoilState(rentalAgreementFillingValueState);
	const [bookingEntity, setBookingEntity] = useRecoilState(bookingDataValueState);
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState<{previous: ICounterSale | null, last: ICounterSale | null}>(counterSaleManagementValueState);
	const [listVehiclesReady, setListVehiclesReady] = useRecoilState(listVehiclesReadyState);
	const recoveryMode = useRecoilValue(recoveryRaModeState);

	const setRaFillingAccordionValidation = useSetRecoilState(raFillingAccordionValidationState);

	const activeTabType = ['person', 'vatNumber', 'company'];

    const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
            return getLocaleByLang(appLanguage.i18n.language);
        }, [appLanguage.i18n.language]);

	const raFillingDataForm = useForm<IRaFillingForm>({
		defaultValues: {
			...deepClone(RA_FILLING_FORM_INITIAL_VALUE),
			keylessArea: null,
		},
		resolver: props.keyLess ?
			yupResolver(RA_FILLING_FORM_VALIDATION_SCHEMA_KEYLESS, {}, { rawValues: true }) :
			yupResolver(RA_FILLING_FORM_VALIDATION_SCHEMA, {}, { rawValues: true })
	});

	const {errors, dirtyFields}: any = raFillingDataForm.formState;
	const {creditType, segment, firstInvoiceRecipient} = bookingEntity.financialInfo;
    const hasSecondBo = isRetail(bookingEntity?.bookingDetail?.rentalType) || [CREDIT_TYPE_VALUES.PARTIAL_CREDIT].includes(creditType);

    const [queryParameters] = useSearchParams();
    const isRce = queryParameters.get("rce") === "1";

	const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(message),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}
	
	/**
	 * Return value of the active TAB( utilizzata in handleNextStep )
	 */
	const controlActiveTab = (data: any, type: 'first' | 'second') => {
		const fromValues = raFillingDataForm.getValues();

		let activeTab = type === 'first' ? fromValues.billingOwnerInfo.billingOwnerActiveTab :  fromValues.billingOwnerInfo.billingOwnerSecondActiveTab;
		if (activeTab === 0) {
			return {
				billingOwnerType: 	BILLING_OWNER_TYPE.PERSON,
				cbId: 				data.billingOwnerInfo[type].person.customerId,
				firstName: 			data.billingOwnerInfo[type].person.firstName,
				lastName: 			data.billingOwnerInfo[type].person.lastName,
				fiscalCode: 		data.billingOwnerInfo[type].person.fiscalCode,
				gender: 			data.billingOwnerInfo[type].person.gender,
				birthDate: 			data.billingOwnerInfo[type].person.birthDate?.format(LOCALE_ISO_DATE_TIME_FORMAT),
				birthNation: 		data.billingOwnerInfo[type].person.birthNation,
				birthPlace: 		!!data.billingOwnerInfo[type].person.birthPlace ?
										{ code: data.billingOwnerInfo[type].person.birthPlace, description: data.billingOwnerInfo[type].person.birthPlace } :
										null,
				countryResidence: 	data.billingOwnerInfo[type].person.countryResidence,
				prefix: 			data.billingOwnerInfo[type].person.prefix,
				mobile: 			data.billingOwnerInfo[type].person.mobile,
				email: 				data.billingOwnerInfo[type].person.firstEmail,
				certifiedEmail: 	data.billingOwnerInfo[type].person.certifiedEmail,
				billingCountry: 	data.billingOwnerInfo[type].person.billingCountry,
				billingProvince: 	data.billingOwnerInfo[type].person.billingProvince,
				billingCity: 		!!data.billingOwnerInfo[type].person.billingCity ?
										{ code: data.billingOwnerInfo[type].person.billingCity, description: data.billingOwnerInfo[type].person.billingCity } :
										null,
				billingAddress: 	data.billingOwnerInfo[type].person.billingAddress,
				billingStreetNumber: data.billingOwnerInfo[type].person.billingStreetNumber,
				billingPostalCode: 	data.billingOwnerInfo[type].person.billingPostalCode,
				drivingLicenseNumber: data.billingOwnerInfo[type].person.drivingLicenseNumber,
			}
		} else if (activeTab === 1) {
			return {
				billingOwnerType: 	BILLING_OWNER_TYPE.VAT_NUMBER,
				cbId: 				data.billingOwnerInfo[type].vatNumber.customerId,
				companyName: 		data.billingOwnerInfo[type].vatNumber.companyName,
				billingCountry: 	data.billingOwnerInfo[type].vatNumber.billingCountry,
				billingProvince: 	data.billingOwnerInfo[type].vatNumber.billingProvince,
				billingCity: 		!!data.billingOwnerInfo[type].vatNumber.billingCity ?
					{ code: data.billingOwnerInfo[type].vatNumber.billingCity, description: data.billingOwnerInfo[type].vatNumber.billingCity } :
					null,
				billingAddress: 	data.billingOwnerInfo[type].vatNumber.billingAddress,
				billingStreetNumber: data.billingOwnerInfo[type].vatNumber.billingStreetNumber,
				billingPostalCode: 	data.billingOwnerInfo[type].vatNumber.billingPostalCode,
				email: 				data.billingOwnerInfo[type].vatNumber.firstEmail,
				certifiedEmail: 	data.billingOwnerInfo[type].vatNumber.certifiedEmail,
				vatNumber: 			data.billingOwnerInfo[type].vatNumber.VATNumber,
				sdicode: 			data.billingOwnerInfo[type].vatNumber.invoiceCode,
				legalEntity:		data.billingOwnerInfo[type].vatNumber.legalEntity,
				vatNumberCountry:	data.billingOwnerInfo[type].vatNumber.vatNumberCountry.description,
				fiscalCode: 		data.billingOwnerInfo[type].vatNumber.fiscalCode,
				linkedToBa: 		!!data.billingOwnerInfo[type].vatNumber?.linkedToBa,
				siren:				data.billingOwnerInfo[type].vatNumber?.siren,
				siret: 				data.billingOwnerInfo[type].vatNumber?.siret,
			}
		} else if (activeTab === 2) {
			return {
				billingOwnerType: 	BILLING_OWNER_TYPE.COMPANY,
				cbId:				data.billingOwnerInfo[type].company.customerId,
				companyName: 		data.billingOwnerInfo[type].company.companyName,
				billingCountry: 	data.billingOwnerInfo[type].company.billingCountry,
				billingProvince: 	data.billingOwnerInfo[type].company.billingProvince,
				billingCity: 		!!data.billingOwnerInfo[type].company.billingCity ?
					{ code: data.billingOwnerInfo[type].company.billingCity, description: data.billingOwnerInfo[type].company.billingCity } :
					null,
				billingAddress: 	data.billingOwnerInfo[type].company.billingAddress,
				billingStreetNumber: data.billingOwnerInfo[type].company.billingStreetNumber,
				billingPostalCode: 	data.billingOwnerInfo[type].company.billingPostalCode,
				email: 				data.billingOwnerInfo[type].company.firstEmail,
				certifiedEmail: 	data.billingOwnerInfo[type].company.certifiedEmail,
				vatNumber: 			data.billingOwnerInfo[type].company.VATNumber,
				sdicode: 			data.billingOwnerInfo[type].company.invoiceCode,
				legalEntity:		data.billingOwnerInfo[type].company.legalEntity,
				vatNumberCountry:	data.billingOwnerInfo[type].company.vatNumberCountry.description,
				fiscalCode: 		data.billingOwnerInfo[type].company.fiscalCode,
				linkedToBa: 		!!data.billingOwnerInfo[type].company?.linkedToBa,
				publicAuthority: 	!!data.billingOwnerInfo[type].company?.publicAuthority?.code ? data.billingOwnerInfo[type].company.publicAuthority.code : '0',
				paIdNumber: 		data.billingOwnerInfo[type].company?.publicAuthority?.code === '1' ? (!!data.billingOwnerInfo[type].company.paIdNumber ? data.billingOwnerInfo[type].company.paIdNumber  : PA_ID_NUMBER_DEFAULT_VALUE) : '',
				siren:				data.billingOwnerInfo[type].company?.siren,
				siret: 				data.billingOwnerInfo[type].company?.siret,
			}
		}
	}

	/**
	 *  Returns the value to use as the DefaultValue for Vehicle detail
	 */
	const buildVehicleInfo = () => {
		const info: any = deepClone(RA_FILLING_FORM_INITIAL_VALUE.vehicleDetails);

		if (!!bookingEntity.vehicle?.plate?.code) { // bookingEntity.vehicle // dopo completamento del raFilling
			//console.log('LB cerchiamo veicolo !!!!');
			for (let i = 0; i < listVehiclesReady.length; i++) {
				if (bookingEntity.vehicle.plate.code === listVehiclesReady[i].plate) {
					info.acriss =	{code: listVehiclesReady[i].acrissCode, description: listVehiclesReady[i].acrissLabel};
					info.sipp = 	{code: listVehiclesReady[i].sippCode, description: listVehiclesReady[i].sippLabel};
					info.plate = 	listVehiclesReady[i];

					info.vehicleID = !!listVehiclesReady[i].vin ? listVehiclesReady[i].vin : '';
					info.kmOnStart = !!listVehiclesReady[i].kms ? listVehiclesReady[i].kms : '';
					info.tankLevelOnStart = !!listVehiclesReady[i]?.currentTermicTankFilling ? listVehiclesReady[i].currentTermicTankFilling + '/8' : '';
					info.batteryLevelOnStart = !!listVehiclesReady[i]?.currentElectricTankFilling ? listVehiclesReady[i].currentElectricTankFilling + '%' : '';
					info.enginePowerGroupCode = !!listVehiclesReady[i]?.enginePowerGroupCode ? listVehiclesReady[i].enginePowerGroupCode : '';
					info.rdfiCard = !!listVehiclesReady[i]?.rfidCardNumber ? listVehiclesReady[i].rfidCardNumber : '';
					info.eToll = !!listVehiclesReady[i]?.etollEquipNumber ? listVehiclesReady[i].etollEquipNumber : '';
					info.chargingCableDomesticType = !!listVehiclesReady[i]?.chargingCableDomesticType ? listVehiclesReady[i].chargingCableDomesticType : '';
					info.chargingCableType2 = 		!!listVehiclesReady[i]?.chargingCableType2 ? listVehiclesReady[i].chargingCableType2 : '';
					info.carClassDescription = 		!!listVehiclesReady[i]?.carClassDescription ? listVehiclesReady[i].carClassDescription : '';
					info.carSegmentDescription =	!!listVehiclesReady[i]?.carSegmentDescription ? listVehiclesReady[i].carSegmentDescription : '';
					info.keyless =	                !!listVehiclesReady[i]?.keyless ? listVehiclesReady[i].keyless : '';
					break;
				}
			}
		} else {
			if (!!bookingEntity.bookingDetail?.acrissCode) {
				for (let i = 0; i < listVehiclesReady.length; i++) {
					if (bookingEntity.bookingDetail.acrissCode === listVehiclesReady[i].acrissCode) {
						info.acriss = {code: listVehiclesReady[i].acrissCode, description: listVehiclesReady[i].acrissLabel};
						break;
					}
				}
			}

			if (!!bookingEntity.bookingDetail?.acrissCode) {
				for (let i = 0; i < listVehiclesReady.length; i++) {
					if (bookingEntity.bookingDetail.acrissCode === listVehiclesReady[i].sippCode) {
						info.sipp = {code: listVehiclesReady[i].sippCode, description: listVehiclesReady[i].sippLabel};
						break;
					}
				}
			}
		}
		return info;
	}

	/**
	 *  Returns the value to use as the DefaultValue for RaFilling Form
	 */
	const buildFormDefaultValue = (): IRaFillingForm => {
		let outcome: any = {};
		//console.log('LB raFilling', raFilling);
		if (!_.isEmpty(raFilling.vehicleDetails) ) { // back in raFilling from preauth
			//console.log('LB back in raFilling from preauth');
			const vehicleInfo = buildVehicleInfo();
			// check raFilling.props
			outcome = {
				generalInfo: 		raFilling.generalInfo,
				vehicleDetails: 	{
										...vehicleInfo,
										specialConditions: 	bookingEntity.bookingDetail?.specialConditionAccepted
									},
				billingOwnerInfo: 	raFilling.billingOwnerInfo,
				ancillariesInfo: 	raFilling.ancillariesInfo,
				quotationInfo:  	raFilling.quotationInfo,
				keylessArea:        raFilling.keylessArea
			};
		} else {
			const vehicleInfo = buildVehicleInfo(); 
			const csm = deepClone(counterSaleManagement.last);
			//console.log('buildFormDefaultValue', vehicleInfo);
			//console.log('buildFormDefaultValue', csm);
			const eTollStr = String(raFillingDataForm.getValues('vehicleDetails.eToll') ?? "").trim();
			const hasEToll = eTollStr.length > 0;
			//console.log(hasEToll);
			csm?.counterSaleAncillaryList?.forEach((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === "ET" && !hasEToll) {
					//console.log("3 ET Ancillary found in manageAncillary function");
					ancillary.deselectable = false;
					ancillary.mileage = 0;
					ancillary.units = 0;
				}
			});

			outcome = {
				generalInfo: {
					pickupLocation: 			bookingEntity.bookingDetail?.pickUpLocation,
					expectedPickupDateTime: 	dayjs(bookingEntity.bookingDetail?.pickupDate),
					dropoffLocation: 			bookingEntity.bookingDetail?.dropOffLocation,
					expectedDropoffDateTime: 	dayjs(bookingEntity.bookingDetail?.dropoffDate),
					expectedTotRentalDays: 		bookingEntity.bookingDetail?.totalRentalDays,
					bookedCarGroup: 			`${bookingEntity?.bookingDetail?.bookedAcriss ?? ''} - ${bookingEntity?.bookingDetail?.bookedAcrissCarModelDescription ?? ''}`,
					bookedVehicleType: 			bookingEntity.bookingDetail?.bookedVehicleType,
					upSell: 					!!bookingEntity.upsell ? 'YES' : 'NO',
					billingCarGroup: 			`${bookingEntity?.bookingDetail?.bookedAcriss ?? ''} - ${bookingEntity?.bookingDetail?.bookedAcrissCarModelDescription ?? ''}`
				},
				vehicleDetails: 				{
													...vehicleInfo,
													specialConditions: 	bookingEntity.bookingDetail?.specialConditionAccepted
												},
				billingOwnerInfo: 				{
													billingOwnerActiveTab: 0,
													billingOwnerSecondActiveTab: hasSecondBo ? 0 : -1,
													first: {},
													second: {}
												},
				ancillariesInfo: {
					booked: 					csm.bookedAncillaryList,
					counterSale: 				csm.counterSaleAncillaryList,
				},
				quotationInfo: {
					reservedTariffs: 			bookingEntity.financialInfo?.reservedTariffs ?? false,
					rate: 						bookingEntity.quotationInfo?.rate ?? '',
					rentalAmount: 				formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], csm.rentalAmountVatIncluded + csm.upsellAmountVatIncluded),
					totalAncilliariesAmount: 	formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], csm.totalAncillaryAmount.promoAmountVatIncluded ?? csm.totalAncillaryAmount.amountVatIncluded),
					totalRentalAmountVatExcl: 	formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], csm.totalAmountVatExcluded),
					totalRentalAmountVatIncl: 	formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], csm.totalAmountVatIncluded),
					airportTax: 				formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.airportTax),
					railwayTax: 				formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.railwayTax),
					roadTax: 					formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.roadTax),
					netRentalAmount: 			formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.totRentalAmountBookedVat),

					// Additional info
					damageExcess: 				formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.damageExcess),
					fireTheftExcess: 			formatCurrency(LOCALES[appLanguage.i18n.language as TLocales], bookingEntity.quotationInfo?.fireTheftExcess)
				},
                keylessArea: {
                    id: null,
                    keyLessInfo: '',
                    lat: null,
                    lon: null,
                    radius: null,
                    mobilityStoreId: null
                }
			};

			/** FIRST BillingOwner */
			manageBillingOwnerInfo('first', outcome.billingOwnerInfo);

			if (hasSecondBo) {
				/** SECOND BillingOwner*/
				manageBillingOwnerInfo( 'second', outcome.billingOwnerInfo);
			}
		}

		/** Financial info */
		outcome['financialInfo'] = {
			creditType: 		bookingEntity.financialInfo?.creditType,
			paymentStatus: 		bookingEntity.bookingDetail?.paymentStatus,
			paymentTiming: 		FINANCIAL_PAYMENT_TIMING_CODES.includes(bookingEntity.financialInfo?.paymentTiming) ?
									FINANCIAL_PAYMENT_TIMING[bookingEntity.financialInfo.paymentTiming] :
									'',
			clientPaymentCode: 	bookingEntity.financialInfo?.clientPaymentCode,
			businessAgreement:	bookingEntity.financialInfo?.businessAgreement,
			driverPaymentCode:	bookingEntity.financialInfo?.driverPaymentCode,
			segment:			bookingEntity.financialInfo?.segmentDescription,
			category: 			bookingEntity.financialInfo?.categoryDescription,
			product: 			bookingEntity.financialInfo?.product
		};

		return outcome;
	}

	const updateDraftRaMutation = useMutation(
		raFillingService,
		{
			onSuccess: (response: any) => {
				response.bookingDetail.ancillaries = checkAncillaryUnitsMileage(response.bookingDetail.ancillaries);
				setBookingEntity(response);

				const data = raFillingDataForm.getValues();
				switch(data.billingOwnerInfo.billingOwnerActiveTab) {
					case 0:
						// @ts-ignore
						data.billingOwnerInfo.first.person.customerId = response.billingOwner.customerId; // todo verifcare alexandr, Pirone
						// @ts-ignore
						data.billingOwnerInfo.first.person.firstEmail = response.billingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
						break;
					case 1:
						// @ts-ignore
						data.billingOwnerInfo.first.vatNumber.customerId = response.billingOwner.customerId;
						// @ts-ignore
						data.billingOwnerInfo.first.vatNumber.firstEmail = response.billingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
						break;
					case 2:
						// @ts-ignore
						data.billingOwnerInfo.first.company.customerId = response.billingOwner.customerId;
						// @ts-ignore
						data.billingOwnerInfo.first.company.firstEmail = response.billingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
						break;
				}

				if (hasSecondBo) {
					switch(data.billingOwnerInfo.billingOwnerSecondActiveTab){
						case 0:
							// @ts-ignore
							data.billingOwnerInfo.second.person.customerId = response.secondBillingOwner.customerId; // todo verifcare alexandr, Pirone
							// @ts-ignore
							data.billingOwnerInfo.second.person.firstEmail = response.secondBillingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
							break;
						case 1:
							// @ts-ignore
							data.billingOwnerInfo.second.vatNumber.customerId = response.secondBillingOwner.customerId;
							// @ts-ignore
							data.billingOwnerInfo.second.vatNumber.firstEmail = response.secondBillingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
							break;
						case 2:
							// @ts-ignore
							data.billingOwnerInfo.second.company.customerId = response.secondBillingOwner.customerId;
							// @ts-ignore
							data.billingOwnerInfo.second.company.firstEmail = response.secondBillingOwner.email; // todo modificare interfaccia form=>  sostituire firstEmail con prop email
							break;
					}
				}


				setRaFilling({
					generalInfo: 	data.generalInfo,
					vehicleDetails: data.vehicleDetails.plate,
					financialInfo: 	data.financialInfo,
					billingOwnerInfo:   data.billingOwnerInfo,
					ancillariesInfo:    data.ancillariesInfo,
					quotationInfo: 	    data.quotationInfo,
					keylessArea:    data.keylessArea
				});

				if (!response.billingOwner.customerId) {
					viewErrorMessage('raFilling.message.error.invalidCustomerId');
					return;
				}

				// set next step
				const completed = step.completed.concat();
				let steps = 1;
				if (recoveryMode && !!bookingEntity.bookingDetail?.flagSubstitution) {
					steps = 3;
				} else if (!!bookingEntity.bookingDetail?.flagSubstitution) {
					steps = 2
				}

				Array(steps).fill(0).map((elem, i) => completed[step.active + i + 1]);
				setStep({
					active: step.active + steps,
					completed,
					numSteps: step.numSteps
				});
			},
			onError: (err) => {
				let errorMessage = t('customerData.message.error.tryLater');

				if (err instanceof AxiosError) {
					if(Array.isArray(err.response?.data)) {
						err.response?.data.forEach?.((e: any) => {
							switch (e.code) {
								case 'billing_owner_fiscal_code':
									errorMessage = t('raFilling.billingOwnerInfo.message.error.fiscalCode');
									break;
								case 'billing_owner_duplicate':
									errorMessage = t(`raFilling.billingOwnerInfo.message.error.billingOwnerPersonDuplicate.${activeTabType[raFillingDataForm.getValues().billingOwnerInfo.billingOwnerActiveTab]}`, {number: 'first'});
									break;
								case 'second_billing_owner_duplicate':
									errorMessage = t(`raFilling.billingOwnerInfo.message.error.billingOwnerPersonDuplicate.${activeTabType[raFillingDataForm.getValues().billingOwnerInfo.billingOwnerSecondActiveTab]}`, {number: 'second'});
									break;
							}
						})
					} else if ((err?.response?.data)?.hasOwnProperty('errorDetails')) {
                        const {errorDetails, errorMessage: errorAddMessage} = (err?.response?.data);

						if (err.response?.status === 400) {
							if (err.response?.data?.errorDetails === "\"ERROR_SAP_CREATION_NOT_ALLOWED\"") {
								errorMessage = t("common.error.errorSapCreationNotAllowed");
							} else if (err.response?.data?.errorDetails === "\"ERROR_SAP_UPDATE_NOT_ALLOWED\"") {
								errorMessage = t("common.error.errorSapUpdateNotAllowed");
							} else if (err.response?.data?.errorDetails === "\"ERROR_SAP_UPDATING_PERMISSION_DENIED\"") {
								errorMessage = t("common.error.errorSapUpdatingPermissionDanied");
							} else if (err.response?.data?.errorDetails === "\"ERROR_FIRST_BILLING_OWNER_BPCODE_EMPTY\"") {
								errorMessage = t("common.error.firstBillingOwnerBPCodeEmpty");
							} else if (err.response?.data?.errorDetails === "\"ERROR_SECOND_BILLING_OWNER_BPCODE_EMPTY\"") {
								errorMessage = t("common.error.secondBillingOwnerBPCodeEmpty");
							};
						} else if(!!ERROR_DETAIL_LIST?.hasOwnProperty(errorDetails)) {
							const errorDetailLs = ERROR_DETAIL_LIST[errorDetails as keyof typeof ERROR_DETAIL_LIST];
							if('addingMessage' in errorDetailLs) {
                                const detail = errorDetailLs.addingMessage(errorAddMessage);
								errorMessage = t(`customerData.message.error.${errorDetailLs.message}`, {detail});
							} else {
								errorMessage = t(`customerData.message.error.${errorDetailLs.message}`);
							}
                        } else if (errorDetails === "\"ERROR_VEHICLE_NOT_INFLATED\"") {
							errorMessage = t("common.error.vehicleNotInflated");
						}
                    }
				}
				viewErrorMessage(errorMessage);
			}
		}
	);

	const setBillingAcrissValue = (acriss: {code: string, description: string} | null) => {
		if (!!acriss) {
			raFillingDataForm.setValue('generalInfo.billingCarGroup', `${acriss.code} - ${acriss.description}`);
		} else {
			const bookedAcriss = raFillingDataForm.getValues('generalInfo.bookedCarGroup');
			raFillingDataForm.setValue('generalInfo.billingCarGroup', bookedAcriss);
		}
	}

	const counterSaleManagementMutation = useMutation(
		fetchCounterSaleManagement,
		{
			onSuccess: async (response: ICounterSale) => {
				if (flagUpdateDefaultFormValue) {
					if (response.upgrade) {
						if (bookingEntity?.bookingDetail?.substitution) {
							viewErrorMessage("raFilling.message.error.substitution.upgradeWarning", snackbarTypes.warning);
						} else {
							viewErrorMessage("raFilling.message.error.upsell.upgradeWarning", snackbarTypes.warning);
						}
					}
					// NOTA: deve essere fatto, visto che si cambiano i dati
					// set quotation values
					raFillingDataForm.setValue('quotationInfo.rentalAmount', 				formatCurrency(locale as LOCALES, response.rentalAmountVatIncluded + response.upsellAmountVatIncluded));
					raFillingDataForm.setValue('quotationInfo.totalAncilliariesAmount',   formatCurrency(locale as LOCALES, response.totalAncillaryAmount.promoAmountVatIncluded ?? response.totalAncillaryAmount.amountVatIncluded));
					raFillingDataForm.setValue('quotationInfo.totalRentalAmountVatExcl', 	formatCurrency(locale as LOCALES, response.totalAmountVatExcluded));
					raFillingDataForm.setValue('quotationInfo.totalRentalAmountVatIncl', 	response?.promoRentalAmountVatIncludedTotal === 0 ? formatCurrency(locale as LOCALES, response.totalAmountVatIncluded) : formatCurrency(locale as LOCALES, response.promoRentalAmountVatIncludedTotal));
					raFillingDataForm.setValue('quotationInfo.deltaDiscounts',			formatCurrency(locale as LOCALES, response.appliedDiscounts));

					// manage downgrade/upgrade
					const selectedAcriss = raFillingDataForm.getValues('vehicleDetails.acriss');
					setBillingAcrissValue({
                        code: response?.billingAcriss,
                        description: response?.billingAcrissCarModelDescription
                    });
				}

				
				const eTollStr = String(raFillingDataForm.getValues('vehicleDetails.eToll') ?? "").trim();
				const hasEToll = eTollStr.length > 0;
				//console.log(hasEToll);
				response?.counterSaleAncillaryList?.forEach((ancillary: ICSAncillary) => {
					if (ancillary.ancillaryCode === "ET" && !hasEToll) {
						//console.log("3 ET Ancillary found in manageAncillary function");
						ancillary.deselectable = false;
						ancillary.mileage = 0;
						ancillary.units = 0;
					}
				});
				
				//Utilizzato per aggiornare i valori del foprm delle ancillaries aggiunte nelle pagine precedenti (dopo il back)
				if (!!counterSaleManagement.last) {
					raFillingDataForm.setValue("ancillariesInfo", {
						booked: 					response.bookedAncillaryList,
						counterSale: 				response.counterSaleAncillaryList
					});
				}

				await setCounterSaleManagement({previous: response, last: response});
				setFlagCounterSaveGetOn(null);

				if (!ntwOfferId) {
					const raNtwOfferIdLS = localStorage.getItem('raNtwOfferId');
					const raNtwOfferId = !!raNtwOfferIdLS ? JSON.parse(raNtwOfferIdLS) : {};

					raNtwOfferId[bookingEntity.bookingDetail.idBooking] = response.ntwOfferId;
					localStorage.setItem('raNtwOfferId', JSON.stringify(raNtwOfferId));

					setNtwOfferId(response.ntwOfferId);
				}
			},
			onError: async (error: any) => {
				const csm = deepClone(counterSaleManagement);
				csm.last = deepClone(counterSaleManagement.previous);
				await setCounterSaleManagement(csm);

				// set form value upsell // by Leandro
				raFillingDataForm.setValue('vehicleDetails.acriss', null);
				raFillingDataForm.setValue('vehicleDetails.sipp', null);
				raFillingDataForm.setValue('vehicleDetails.plate', null);

				//ERROR_ON_TAX_CONFIGURATION
				viewErrorMessage(raFillingPricingErrorsManagement(error));

				setFlagCounterSaveGetOn(null);
			}
		}
	);

	const listVehiclesReadyToRentMutation = useMutation(
		listVehiclesReadyToRent,
		{
			onSuccess: (response: any) => {
				if (!response[0]?.vehicles) {
					setListVehiclesReady([]);
					return;
				}

				const list = response[0]?.vehicles?.map((elem: any) => {
						elem['fullDescription'] = elem.plate + ' | ' + elem.brandDescription + ' | ' + elem.modelDescription + ' | ' + elem.enginePowerGroupCode;
						return elem;
					})

				setListVehiclesReady(list);
			},
			onError: async (error: any) => {
				viewErrorMessage('customerData.message.error.tryLater');
			}
		}
	);

	const invokeCounterSaleManagement = async (payload: any) => {
		counterSaleManagementMutation.mutate(payload);
	}

	const handleNextStep = (data: any) => {
		let changesData: string[] = [];
		const firstActiveTab = raFillingDataForm.getValues('billingOwnerInfo.billingOwnerActiveTab');
		const secondActiveTab = raFillingDataForm.getValues('billingOwnerInfo.billingOwnerSecondActiveTab');

		//@ts-ignore
		getChengesData(changesData, 'billingOwner', dirtyFields.billingOwnerInfo?.first?.[activeTabType[firstActiveTab]]);
		//@ts-ignore
		getChengesData(changesData, 'secondBillingOwner', dirtyFields.billingOwnerInfo?.second?.[activeTabType[secondActiveTab]]);

		const secondBillingOwner = hasSecondBo ? controlActiveTab(data, 'second') : null;
		const reqParameters: any = {
			updateRa: counterSaleManagement.last?.updateRa,
			keyless: props.keyLess,
			keyLessInfo: data.keylessArea?.id,
			rce: isRce,
			params: {
				changesData: changesData,
				idRentalAgreement: bookingEntity.idRentalAgreement,
				vehicle: {
					idTenant: bookingEntity.idTenant,
					acrissId: data.vehicleDetails.plate.acrissCode,
					acrissDescription: data.vehicleDetails.plate.acrissLabel,
					sippId: data.vehicleDetails.plate.sippCode,
					sippDescription: data.vehicleDetails.plate.sippLabel,
					brandDescription: data.vehicleDetails.plate.brandDescription,
					carClassDescription: data.vehicleDetails.plate.carClassDescription,
					carSegmentDescription: data.vehicleDetails.plate.carSegmentDescription,
					currentTermicTankFilling: data.vehicleDetails.plate.currentTermicTankFilling,
					currentElectricTankFilling: data.vehicleDetails.plate.currentElectricTankFilling,
					enginePowerGroupCode: data.vehicleDetails.plate.enginePowerGroupCode,
					internalNumber: data.vehicleDetails.plate.internalNumber,
					kilometersOnStart: data.vehicleDetails.plate.kms,
					modelDescription: data.vehicleDetails.plate.modelDescription,
					electricTankCapacity: data.vehicleDetails.plate.electricTankCapacity * 100,
					termicTankCapacity: data.vehicleDetails.plate.termicTankCapacity * 100,
					plate: {
						code: data.vehicleDetails.plate.plate,
						description: data.vehicleDetails.plate.plate,
						label: data.vehicleDetails.plate.plate,
					},
					vin: data.vehicleDetails.plate.vin,
					chargingCableDomesticType: data.vehicleDetails?.plate.chargingCableDomesticType === 'YES',
					chargingCableType2: data.vehicleDetails?.plate.chargingCableType2 === 'YES',
					rfidCard: data.vehicleDetails?.plate?.rfidCardNumber,
					etollEquipmentNumber: data.vehicleDetails?.plate?.etollEquipNumber,
				},
				billingOwner: controlActiveTab(data, 'first'),
				secondBillingOwner,
			}
		}
		updateDraftRaMutation.mutate(reqParameters);
	}

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		// FIXME: store Data ra filling
		// const data = raFillingDataForm.getValues();
		// setRaFilling({
		// 	generalInfo: 		data.generalInfo,
		// 	vehicleDetails: 	data.vehicleDetails.plate,
		// 	financialInfo: 		data.financialInfo,
		// 	billingOwnerInfo: 	data.billingOwnerInfo,
		// 	ancilliariesInfo: 	data.ancillariesInfo,
		// 	quotationInfo: 		data.quotationInfo
		// });
		setCounterSaleManagement({previous: null, last: counterSaleManagement.last});
		setStep({
			active: step.active - (recoveryMode ? 2 : 1),
			completed: step.completed,
			numSteps: step.numSteps
		});
	}

	const manageBillingOwnerInfo = (bo: 'first' | 'second', outcomeBO: any) => {
		const billingTypeTab = bo === 'first' ? 'billingOwnerActiveTab' : 'billingOwnerSecondActiveTab';
		const curBillingOwner = bo === 'first' ? bookingEntity.billingOwner : bookingEntity.secondBillingOwner;
		const isBaPerson = !bookingEntity.billingOwner?.legalEntity;
		const isBaVatNumberHolder = bookingEntity.billingOwner?.legalEntity?.code === COMPANY_CODES.DITTA_INDIVIDUALE;
		const isRaPrepaid = segment === SEGMENT_VALUES.SUBSCRIPTION || (segment === SEGMENT_VALUES.RETAIL && bookingEntity.financialInfo.paymentTiming === '1');
		const isRaPoa = segment === SEGMENT_VALUES.RETAIL && bookingEntity.financialInfo.paymentTiming === '2';
		const isRaTourOperator = segment === SEGMENT_VALUES.TOUR_OPERATOR;

		let boType = 0;
		let boTypeExtended: 'person' | 'vatNumber' | 'company' = 'person';

		if (!curBillingOwner) {
			outcomeBO[billingTypeTab] = 0;
			outcomeBO[bo]['person'] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
			completeBillingOwnerAssignmentOfDefaultValues(bo, outcomeBO);
			return;
		}

		let localCreditType = creditType;
		if (!localCreditType) {
			localCreditType = CREDIT_TYPE_VALUES.NO_CREDIT;
		}

		switch (localCreditType) {
			case CREDIT_TYPE_VALUES.PUBLIC:
				boType = 0;
				boTypeExtended = 'person';
				outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
				break;

			case CREDIT_TYPE_VALUES.FULL_CREDIT: //full credit has only first bo
				if (isBaPerson) {
					boType = 0;
					boTypeExtended = 'person';
					outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 0);
				} else if (isBaVatNumberHolder) {
					boType = 1;
					boTypeExtended = 'vatNumber';
					outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 1);
				} else { //is company case
					boType = 2;
					boTypeExtended = 'company';
					outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 2);
				}
				break;

			case CREDIT_TYPE_VALUES.PARTIAL_CREDIT:
				if (bo === 'first') {
					if (isBaPerson) {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 0);
					} else if (isBaVatNumberHolder) {
						boType = 1;
						boTypeExtended = 'vatNumber';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 1);
					} else { //is company case
						boType = 2;
						boTypeExtended = 'company';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 2);
					}
				} else if (bo === 'second') {
					if (isBaPerson) {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
					} else if (isBaVatNumberHolder) {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
					} else { //is company case
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
					}
				}
				break;

			case CREDIT_TYPE_VALUES.NO_CREDIT: //no credit has only first bo or first and second has the same logic
				if (isRaPrepaid) {
					if (curBillingOwner?.legalEntity?.code === COMPANY_CODES.DITTA_INDIVIDUALE) {
						boType = 1;
						boTypeExtended = 'vatNumber';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 1);
					} else if (curBillingOwner?.billingOwnerType === INVOICE_TYPES.COMPANY) {
						boType = 2;
						boTypeExtended = 'company';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 2);
					} else {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 0);
					}
				} else if (isRaPoa) {
					boType = 0;
					boTypeExtended = 'person';
					outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
				} else if (isRaTourOperator) {
					boType = 0;
					boTypeExtended = 'person';
					outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
				} else {
					if (isBaPerson) {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
					} else if (isBaVatNumberHolder) {
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
						boTypeExtended = 'vatNumber';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 1);
					} else { //is company case
						boType = 0;
						boTypeExtended = 'person';
						outcomeBO[bo][boTypeExtended] = mapRaOwnerToPersonBillingOwner(bookingEntity.raOwner);
						boTypeExtended = 'company';
						outcomeBO[bo][boTypeExtended] = currentBillingOwner(curBillingOwner, 2);
					}
				}
				break;
		}

		outcomeBO[billingTypeTab] = boType;
		completeBillingOwnerAssignmentOfDefaultValues(bo, outcomeBO);
	}

	const completeBillingOwnerAssignmentOfDefaultValues = (bo: 'first' | 'second', outcomeBO: any) => {
		const initValue = deepClone(RA_FILLING_FORM_INITIAL_VALUE);
		// set default value of the not eneble tabs
		if (!outcomeBO[bo]['person']) {
			outcomeBO[bo]['person'] = initValue.billingOwnerInfo[bo].person;
		}

		if (!outcomeBO[bo]['vatNumber']) {
			outcomeBO[bo]['vatNumber'] = initValue.billingOwnerInfo[bo].vatNumber;
		}

		if (!outcomeBO[bo]['company']) {
			outcomeBO[bo]['company'] = initValue.billingOwnerInfo[bo].company;
		}
	}

	useQuery({
		queryKey: ['ntw_legal_entities'],
		queryFn: fetchLegalEntities,
	});

	useEffect(() => {
        //console.log(errors);
		if (!!errors && !_.isEmpty(errors)) {
			setRaFillingAccordionValidation({
				generalInfo: (Boolean(errors.generalInfo) || Boolean(errors.vehicleDetails)),
				firstBillingOwnerInfo: Boolean(errors.billingOwnerInfo?.first),
				secondBillingOwnerInfo: Boolean(errors.billingOwnerInfo?.second),
				ancillariesInfo: Boolean(errors.ancillariesInfo)
			});

			viewErrorMessage('common.error.requiredFieldsNotFilledIn', snackbarTypes.warning);
		}
	}, [errors]);

	useEffect(() => {
		if (!_.isEqual(counterSaleManagement.previous, counterSaleManagement.last) && !!counterSaleManagement.last && !counterSaleManagementMutation.isLoading) {
			const formValue = raFillingDataForm.getValues();
			const selected = buildSelectedAcrissSipp(formValue.vehicleDetails);

			const cs = deepClone(counterSaleManagement);
			//console.log('invokeCounterSaleManagement ', cs);
			const eTollStr = String(raFillingDataForm.getValues('vehicleDetails.eToll') ?? "").trim();
			const hasEToll = eTollStr.length > 0;
			//console.log(hasEToll);
			cs?.last?.counterSaleAncillaryList?.forEach((ancillary: ICSAncillary) => {
				if (ancillary.ancillaryCode === "ET" && !hasEToll) {
					//console.log("3 ET Ancillary found in manageAncillary function");
					ancillary.deselectable = false;
					ancillary.mileage = 0;
					ancillary.units = 0;
				}
			});

			const csM = {
				idRentalAgreement: 		bookingEntity.idRentalAgreement,
				upsell: 				formValue.generalInfo.upSell === 'YES',
				selectedAcrissSipp: 	selected,
				ntwOfferId: 			cs.last.ntwOfferId,
				bookedAncillaryList: 	cs.last.bookedAncillaryList.map((elem: any) => {return {ancillaryCode: elem.ancillaryCode, units: elem.units, mileage: elem.mileage, address: elem.address, ancillarySubType: elem.ancillarySubType || elem.subtype, discountPercentage: elem.discountPercentage != null ? elem.discountPercentage * 100 : null}}),
				counterSaleAncillaryList: cs.last.counterSaleAncillaryList.map((elem: any) => {return {ancillaryCode: elem.ancillaryCode, units: elem.units, mileage: elem.mileage, address: elem.address, ancillarySubType: elem.ancillarySubType || elem.subtype, discountPercentage: elem.discountPercentage != null ? elem.discountPercentage * 100 : null}}),
				init: 0,
				etollNumber: formValue.vehicleDetails?.eToll || '',
				idsProfile: buildOperatorProfileIds()
			};
			invokeCounterSaleManagement(csM);
		}
	}, [counterSaleManagement]);

	const setFormDefaultValue = async () => {
		const defaultValue = buildFormDefaultValue();
		raFillingDataForm.reset(defaultValue);
		await setFlagUpdateDefaultFormValue(true);
	}

	useEffect(() => {
		//Utilizzato solo per set dei valori di default della form
		if (!flagUpdateDefaultFormValue && !!bookingEntity && !!counterSaleManagement.last && listVehiclesReady) {
			setFormDefaultValue();
		}
	}, [bookingEntity, counterSaleManagement, listVehiclesReady])

	useEffect(() => {
		if (!!flagCounterSaveGetOn) {
			invokeCounterSaleManagement(flagCounterSaveGetOn);
		}
	}, [flagCounterSaveGetOn]);

	useEffect(() => {
		if (!counterSaleManagement?.last?.ntwOfferId && !counterSaleManagementMutation.isLoading) {
			let ntwOfferId = '';
			let raNtwOfferIdLS = localStorage.getItem('raNtwOfferId');

			if (!!raNtwOfferIdLS) {
				raNtwOfferIdLS = JSON.parse(raNtwOfferIdLS);
				if (!!raNtwOfferIdLS?.[bookingEntity.bookingDetail.idBooking]) {
					ntwOfferId = raNtwOfferIdLS[bookingEntity.bookingDetail.idBooking];
				}
			}

			const payload = {
				idRentalAgreement: 	bookingEntity.idRentalAgreement,
				upsell: 			false, //formValue.generalInfo.upSell === 'YES',
				selectedAcrissSipp: null, //selected,
				ntwOfferId: ntwOfferId,
				bookedAncillaryList: bookingEntity.bookingDetail.ancillaries
										.map((elem: any) => {return {ancillaryCode: elem.ancillaryCode, units: elem.ancillaryUnits, mileage: elem.ancillaryMileage, address: elem.address, ancillarySubType: elem.ancillarySubType}}),
				counterSaleAncillaryList: [],
				init: 1,
				idsProfile: buildOperatorProfileIds()
			};
			setFlagCounterSaveGetOn(payload);
			setNtwOfferId(ntwOfferId);
		}

		if (!listVehiclesReady) {
			listVehiclesReadyToRentMutation
				.mutate({
					tenant: bookingEntity.idTenant,
					locations: [{
						id: bookingEntity?.bookingDetail?.pickUpLocation?.code,
						type: "LMS"
					}],
					"keyless": props.keyLess,
					"rce": isRce,
					bookingId: bookingEntity.bookingDetail.idBooking,
				} as any);
		}
	}, [])

	return (
		<>
			<FormProvider {...raFillingDataForm}>
				<GeneralInfo keyLess={props.keyLess} keyLessAreas={props.keyLessAreas} />

				<FinancialInfo />

				<BillingOwnerInfo boType="first" keyless={props.keyLess} />

				{hasSecondBo && <BillingOwnerInfo boType="second" keyless={props.keyLess} />}

				<AncillariesInfo />

				<QuotationInfo />

				<Box sx={{display: 'flex',  justifyContent: 'space-between', mt: theme.spacing(2)}}>
					{
						step.active === 0 ?
							<Box /> :
							<Button variant="text" color="primary"  onClick={handleBackStep}>{t('common.button.backStep')}</Button>
					}
					{
						step.active ===  (step.numSteps -1) ?
							<Box /> :
							<Button variant="outlined"
									color="accent"
									disabled={!counterSaleManagement?.last}
									onClick={raFillingDataForm.handleSubmit((data) => handleNextStep(data))}>
								{t('common.button.nextStep')}
							</Button>
					}
				</Box>
			</FormProvider>

			<CustomLoader loading={updateDraftRaMutation.isLoading || counterSaleManagementMutation.isLoading || listVehiclesReadyToRentMutation.isLoading}/>

		</>
	)
}

export default RaFilling;
